import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from 'material-react-table';
import api from '../../../redux/features/auth/axiosAuthHelper';
import { type TableCriteria } from './tableQuery';
import { apiError, type ApiError } from '../../../services/apiError';

type Change<T> = T | ((previous: T) => T);
function nextState<T>(change: Change<T>, previous: T): T {
  return typeof change === 'function' ? (change as (previous: T) => T)(previous) : change;
}

interface ServerTableOptions<T> {
  endpoint: string;
  resource: string;
  params: (criteria: TableCriteria) => URLSearchParams;
  validRow: (value: unknown) => value is T;
}

export function useServerTable<T extends { id: number }>(enabled: boolean, access: string | null | undefined, options: ServerTableOptions<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [reload, setReload] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const requestId = useRef(0);

  const onColumnFiltersChange = useCallback((change: Change<MRT_ColumnFiltersState>) => {
    setColumnFilters(previous => nextState(change, previous));
    setPagination(previous => ({ ...previous, pageIndex: 0 }));
    setRowSelection({});
  }, []);

  const onGlobalFilterChange = useCallback((change: Change<string>) => {
    setGlobalFilter(previous => nextState(change, previous));
    setPagination(previous => ({ ...previous, pageIndex: 0 }));
    setRowSelection({});
  }, []);

  const onSortingChange = useCallback((change: Change<MRT_SortingState>) => {
    setSorting(previous => nextState(change, previous).slice(0, 1));
    setPagination(previous => ({ ...previous, pageIndex: 0 }));
  }, []);

  const onPaginationChange = useCallback((change: Change<MRT_PaginationState>) => {
    setPagination(previous => {
      const next = nextState(change, previous);
      return next.pageSize !== previous.pageSize ? { ...next, pageIndex: 0 } : next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setColumnFilters([]);
    setGlobalFilter('');
    setPagination(previous => ({ ...previous, pageIndex: 0 }));
    setRowSelection({});
  }, []);

  const refresh = useCallback(() => {
    if (!error?.retryAt || Date.now() >= error.retryAt) setReload(value => value + 1);
  }, [error]);
  const criteria = useMemo(() => ({ sorting, columnFilters, globalFilter, pagination }), [sorting, columnFilters, globalFilter, pagination]);
  const query = useMemo(() => options.params(criteria).toString(), [criteria, options]);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setRowCount(0);
      setRowSelection({});
      setHasLoaded(false);
      setIsLoading(false);
      return;
    }
    const currentRequest = ++requestId.current;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    // MRT already debounces text input. Page, sort and retry requests start immediately.
    const load = async () => {
      try {
        const response = await api.get<unknown>(`${options.endpoint}/?${query}`, {
          headers: { Authorization: `Bearer ${access}` },
          signal: controller.signal,
          timeout: 30000,
        });
        if (currentRequest !== requestId.current) return;
        if (!validResponse(response.data, options.validRow) || response.data.results.length > pagination.pageSize ||
            response.data.results.length > response.data.count) {
          setError({ fields: {}, message: `${options.resource} could not be displayed because the server response was invalid. Please try again.` });
          return;
        }
        const { count, results } = response.data;
        const lastPage = Math.max(0, Math.ceil(count / pagination.pageSize) - 1);
        if (pagination.pageIndex > lastPage) {
          setPagination(previous => ({ ...previous, pageIndex: lastPage }));
          return;
        }
        setRows(results);
        setRowCount(count);
        setHasLoaded(true);
      } catch (failure: unknown) {
        if (currentRequest !== requestId.current || controller.signal.aborted) return;
        const loadError = apiError(failure, options.resource);
        setError(loadError);
        if (loadError?.status === 401 || loadError?.status === 403) {
          setRows([]);
          setRowCount(0);
          setRowSelection({});
          setHasLoaded(false);
        }
      } finally {
        if (currentRequest === requestId.current) setIsLoading(false);
      }
    };
    void load();
    return () => {
      requestId.current += 1;
      controller.abort();
    };
  }, [enabled, access, query, reload, pagination.pageIndex, pagination.pageSize, options]);

  return {
    rows, rowCount, isLoading, hasLoaded, error, sorting, columnFilters, globalFilter,
    pagination, rowSelection, setRowSelection, onSortingChange,
    onColumnFiltersChange, onGlobalFilterChange, onPaginationChange,
    clearFilters, refresh,
  };
}

function validResponse<T extends { id: number }>(value: unknown, validRow: (row: unknown) => row is T): value is { count: number; results: T[] } {
  const data = value as { count: number; results: T[] } | null;
  return !!data && Number.isSafeInteger(data.count) && data.count >= 0 &&
    Array.isArray(data.results) && data.results.every(row => validRow(row) && Number.isSafeInteger(row.id) && row.id > 0) &&
    new Set(data.results.map(row => row.id)).size === data.results.length;
}
